import { getApiHost } from 'base/api_host';
import { delay, UnreachableError } from 'base/helpers';
import { ServiceHttpError } from 'base/service_error';
import { formatError } from 'base/ui/form';
import { zodParse } from 'base/zod_parse';
import {
  BlocksService,
  ClientMessage,
  ServerInitMessage,
  ServerMessage,
  serverMessageSchema,
} from 'blocks/blocks_service';
import { MODEL_SCHEMA_VERSION, projectDomain } from 'blocks/editor/model';
import { ConfigOf } from 'blocks/woot_woot/base';
import { SuffixedClock, TimeClock } from 'blocks/woot_woot/clock';
import { AnyDelta, compressDeltas } from 'blocks/woot_woot/deltas';
import { UndoManagerImpl } from 'blocks/woot_woot/undo';
import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableSet,
  when,
} from 'mobx';
import { CommentsStore } from './comments_store';
import {
  PresenceDecorationKVStore,
  PresenceDecorations,
} from './presence_decorations';
import { PresenceTracker } from './presence_tracker';

/**
 * Simple wrapper over a web socket that allows for message buffering and
 * error handling.
 */
class InitializingWebsocket {
  @observable.ref
  state: 'initializing' | 'ready' | 'closed' = 'initializing';
  lastError: unknown;
  @observable.ref
  fatalError?: unknown;

  private ws!: WebSocket;
  private recreateScheduled = false;
  private buffer: string[] = [];

  constructor(
    /**
     * Function which returns the initial message that should be sent to the
     * server.
     */
    private initMessage: () => ClientMessage & { type: 'init' },
    private readonly handleMessage: (msg: ServerMessage) => void,
    private readonly handleServiceError: (error: ServiceHttpError) => void,
  ) {
    makeObservable(this);
    this.createSocket();
  }

  send(msg: ClientMessage) {
    const data = JSON.stringify(msg);
    if (this.state === 'ready') {
      this.ws.send(data);
    } else {
      this.buffer.push(data);
    }
  }

  close() {
    this.state = 'closed';
    this.ws.close();
  }

  private createSocket = () => {
    const apiHost = getApiHost();
    if (!apiHost.startsWith('http')) throw new Error();

    if (this.ws) {
      this.ws.removeEventListener('open', this.onOpen);
      this.ws.removeEventListener('message', this.onMessage);
      this.ws.removeEventListener('close', this.onClose);
      this.ws.removeEventListener('error', this.onError);
      try {
        this.ws.close();
      } catch (e) {}
    }
    if (this.state === 'closed') return;

    this.state = 'initializing';
    this.ws = new WebSocket('ws' + apiHost.slice(4) + 'ws/blocks/project/');
    this.recreateScheduled = false;
    this.ws.addEventListener('open', this.onOpen);
    this.ws.addEventListener('message', this.onMessage);
    this.ws.addEventListener('close', this.onClose);
    this.ws.addEventListener('error', this.onError);
  };

  private onOpen = () => {
    this.ws.send(JSON.stringify(this.initMessage()));
  };

  private onMessage = (e: MessageEvent) => {
    const msgRaw = JSON.parse(e.data);
    const msg = zodParse(serverMessageSchema, msgRaw);
    if (msg.type === 'error') {
      // A backend error probably means our project is stuffed.  Don't
      // reconnect.
      const error = new ServiceHttpError(
        msg.statusCode,
        JSON.stringify(msgRaw),
        'websocket',
      );
      this.lastError = error;
      this.fatalError = error;
      this.handleServiceError(error);
      this.state = 'closed';
      this.createSocket();
      return;
    }
    if (msg.type === 'init') {
      this.state = 'ready';
      this.buffer.forEach((msg) => this.ws.send(msg));
      this.buffer = [];
    }
    this.handleMessage(msg);
  };

  private onError = () => {
    this.lastError = 'check your internet connection.';
    console.warn('[collab] Lost web socket connection due to error');
    if (!this.recreateScheduled) {
      this.recreateScheduled = true;
      setTimeout(this.createSocket, RECONNECT_TIMEOUT_MS);
    }
  };

  private onClose = (e: CloseEvent) => {
    console.warn('[collab] Websocket closed, reconnecting', e.code, e.reason);
    if (!this.recreateScheduled) {
      this.recreateScheduled = true;
      setTimeout(this.createSocket, RECONNECT_TIMEOUT_MS);
    }
  };
}

const RECONNECT_TIMEOUT_MS = 2_000;

type CollabSyncerOptions = {
  userToken: string;
  projectToken: string;
  hideFromPresence?: boolean;
  blocksService: BlocksService;
};

/**
 * Creates a websocket to the backend and manages sending messages back and
 * forth.
 */
export class CollabSyncer {
  /**
   * The undo manager here is responsible for the local copy of the project. It
   * manages applying all changes from the server to the project. It also
   * calls `sendToServer` on CollabSyncer to send local changes to the server.
   */
  readonly undo: UndoManagerImpl<ConfigOf<typeof projectDomain>>;
  private pingTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
  private outstandingDeltas = observable.map<string, AnyDelta>([], {
    deep: false,
  });
  private toSendBuffer: AnyDelta[] = [];
  private sendToServerTimeout: ReturnType<typeof setTimeout> | undefined;

  readonly presence = new PresenceTracker();
  private readonly decorationsKv = new PresenceDecorationKVStore(
    this.ws,
    this.presence,
    this.allSessionIds,
  );
  readonly decorations = new PresenceDecorations(this.decorationsKv);
  readonly comments: CommentsStore;
  @observable.ref
  hasMadeChanges = false;
  @observable.ref
  lastSyncedTimeS = Date.now() / 1000;

  static create(opts: CollabSyncerOptions): Promise<CollabSyncer> {
    let initializedAt: ServerInitMessage | undefined;
    const allSessionIds = observable.set<number>([], { deep: false });
    let impl: CollabSyncer | undefined;

    return new Promise((resolve, reject) => {
      const ws = new InitializingWebsocket(
        /** initMessage */
        () => ({
          type: 'init',
          schemaVersion: MODEL_SCHEMA_VERSION,
          projectToken: opts.projectToken,
          userToken: opts.userToken,
          baseVersion: initializedAt?.baseVersion,
          oldSessionIds: [...allSessionIds],
          hideFromPresence: opts.hideFromPresence || false,
        }),
        /** handleMessage = */
        action((msg) => {
          if (
            msg.type === 'init' &&
            msg.schemaVersion !== MODEL_SCHEMA_VERSION
          ) {
            reject(
              new Error(
                `We're upgrading Relume.  Please refresh the page in a minute.  (Backend schema version ${msg.schemaVersion} does not match client schema version ${MODEL_SCHEMA_VERSION})`,
              ),
            );
            return;
          }
          if (msg.type === 'init') {
            allSessionIds.add(msg.sessionId);
          }
          if (msg.type === 'init' && !impl) {
            initializedAt = msg;
            impl = new CollabSyncer(
              opts.projectToken,
              opts.blocksService,
              ws,
              msg,
              allSessionIds,
            );
            resolve(impl);
            return;
          }
          if (!impl) {
            reject(new Error(`Unexpected message ${msg.type} before init`));
            return;
          }
          impl.handleMessage(msg);
        }),
        reject,
      );

      delay(10_000).then(() => {
        if (impl) return;
        ws.close();
        const err = ws.fatalError || ws.lastError;
        reject(
          new Error(
            `Could not connect to sync server: ${
              err ? formatError(err) : 'timed out'
            }`,
          ),
        );
      });
    });
  }

  private constructor(
    readonly projectToken: string,
    blocksService: BlocksService,
    private readonly ws: InitializingWebsocket,
    readonly initMsg: ServerInitMessage,
    private readonly allSessionIds: ObservableSet<number>,
  ) {
    makeObservable(this);
    this.undo = new UndoManagerImpl(
      projectDomain,
      initMsg.initialState,
      new SuffixedClock(new TimeClock(), `.s${initMsg.sessionId}`),
      this.sendToServer,
    );
    this.comments = new CommentsStore(
      blocksService,
      this.undo,
      this.undo.mutable,
      projectToken,
      this.presence,
    );
    this.handleMessage(initMsg);
  }

  private async handleMessage(msg: ServerMessage) {
    switch (msg.type) {
      case 'init':
        for (const delta of msg.initialDeltas) {
          this.gotDeltaFromServer(delta);
        }
        this.presence.handleMessage(msg);
        this.decorationsKv.handleMessage(msg);
        this.comments.handleMessage(msg);
        // Init might happen multiple times if the websocket reconnects.
        // Resend any pending deltas:
        this.toSendBuffer.push(...this.outstandingDeltas.values());
        this.sendToServerNow();
        this.scheduleNextPing();
        break;
      case 'delta':
        this.gotDeltaFromServer(msg);
        break;
      case 'comment':
      case 'commentThread':
        this.comments.handleMessage(msg);
        break;
      case 'presenceJoin':
      case 'presenceLeave':
        this.presence.handleMessage(msg);
        break;
      case 'presenceDecoration':
        this.decorationsKv.handleMessage(msg);
        break;
      case 'error':
        throw new Error('Unexpected error message from server');
      case 'pong':
        this.scheduleNextPing();
        break;
      default:
        throw new UnreachableError(msg);
    }
  }

  disconnect() {
    this.ws.close();
  }

  private scheduleNextPing() {
    if (this.pingTimeout) clearTimeout(this.pingTimeout);
    this.pingTimeout = setTimeout(this.sendPingNow, PING_INTERVAL_MS);
  }

  private sendPingNow = () => {
    this.pingTimeout = undefined;
    this.ws.send({ type: 'ping' });
  };

  private readonly sendToServer = (deltas: readonly AnyDelta[]) => {
    if (deltas.length === 0) return;
    this.hasMadeChanges = true;
    for (const d of deltas) {
      if (this.outstandingDeltas.has(d.time)) {
        throw new Error('Already sent this delta to server');
      }
      this.outstandingDeltas.set(d.time, d);
    }
    this.toSendBuffer.push(...deltas);
    if (this.sendToServerTimeout == null) {
      this.sendToServerTimeout = setTimeout(
        this.sendToServerNow,
        SEND_THROTTLE_MS,
      );
    }
  };

  private readonly sendToServerNow = () => {
    if (this.sendToServerTimeout != null) {
      clearTimeout(this.sendToServerTimeout);
      this.sendToServerTimeout = undefined;
    }

    const rawDeltas = this.toSendBuffer;
    this.toSendBuffer = [];
    const deltas = compressDeltas(rawDeltas);
    if (deltas.length !== rawDeltas.length) {
      const retained = new Set(deltas.map((d) => d.time));
      for (const d of deltas) {
        if (retained.has(d.time)) continue;
        // Doesn't need to be synced to server any more:
        this.outstandingDeltas.delete(d.time);
      }
    }
    if (deltas.length === 0) return;
    this.ws.send({
      type: 'submitDelta',
      data: rawDeltas,
    });
  };

  private gotDeltaFromServer(msg: ServerMessage & { type: 'delta' }) {
    for (const d of msg.data) {
      this.outstandingDeltas.delete(d.time);
    }
    this.undo.applyFromServer(msg.data);
    this.lastSyncedTimeS = Date.now() / 1000;
  }

  syncNow() {
    return when(() => this.outstandingDeltas.size === 0);
  }

  @computed
  get hasUnsavedChanges() {
    return this.outstandingDeltas.size > 0;
  }

  get fatalError() {
    return this.ws.fatalError;
  }

  get isOffline() {
    return !navigator.onLine;
  }
}

const SEND_THROTTLE_MS = 500;
const PING_INTERVAL_MS = 30_000;
